import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { 
  FiTarget, FiShield, FiEye, FiZap, 
  FiMap, FiActivity, FiAward, FiCpu 
} from 'react-icons/fi';
import { FaSkull, FaGamepad } from 'react-icons/fa';
import MicrosoftStoreBtn from '../components/MicrosoftStoreBtn';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';
import { Link } from 'react-router-dom';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-purple/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-purple/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-purple" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const EldritchDescent = () => {
  const PRODUCT = getProduct('eldritch-descent');
  const STORE_LINK = PRODUCT.storeLink;
  const PRICE = PRODUCT.price;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-purple/10 border border-accent-purple/20 text-accent-purple font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FiCpu} /> Lovecraftian Arcade Space Shooter
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Eldritch<br />
              <span className="text-accent-purple">Descent</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "How deep can you descend?"
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <MicrosoftStoreBtn 
                accentColor="bg-accent-purple" 
                className="px-10 py-4 scale-110" 
                href={STORE_LINK}
              />
              <div className="flex flex-col items-start text-left">
                <span className="text-white font-bold">{PRICE}</span>
                <span className="text-xs text-muted">Lifetime License • Windows 10/11</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* The successor */}
      <section className="py-10 border-t border-border">
        <div className="max-w-5xl mx-auto px-4">
          <Link to="/products/eldritch-descent-x" className="grid md:grid-cols-[2fr_3fr] gap-6 items-center bg-card border border-border hover:border-accent-purple/50 transition-all p-4 decoration-transparent">
            <img src="/reel/eldritch-descent-x-2.webp" alt="Eldritch Descent X: the Void Titan" className="w-full aspect-video object-cover" loading="lazy" />
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-accent-purple mb-2">Coming soon · the successor</p>
              <h2 className="text-3xl font-black text-white uppercase italic mb-2">Eldritch Descent X</h2>
              <p className="text-muted text-sm leading-relaxed">Rebuilt from scratch: five drowned strata, guardians and lords, a sanity system that lies to you and two-pilot co-op. Watch the trailer.</p>
            </div>
          </Link>
        </div>
      </section>

      {/* Description Section */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            Fight Shoggoths, Deep Ones and Ancient Ones across 9 challenging levels. Master an arsenal of 8 devastating weapons and harness dark corruption powers to survive the void.
          </p>
        </div>
      </section>

      {/* Reel — the frames for this product, so a visitor sees what it does. */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Eldritch Descent</p>
          <Reel frames={PRODUCT.frames} label="Eldritch Descent" sound={false} ratio="aspect-[16/10]" />
        </div>
      </section>

      {/* Mechanics Grid */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">Pure arcade action meets cosmic dread.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={FiMap} 
              title="9 Levels of Hell" 
              desc="Battle through procedural environments from the Sunken City to the Cosmic Void." 
            />
            <FeatureCard 
              icon={FiZap} 
              title="8 Unique Weapons" 
              desc="From rapid-fire plasma to experimental dark energy beams. Fully upgradable." 
            />
            <FeatureCard 
              icon={FiActivity} 
              title="Corruption Powers" 
              desc="Sacrifice your sanity to unleash devastating screen-clearing cosmic abilities." 
            />
            <FeatureCard 
              icon={FaGamepad} 
              title="Pro Controls" 
              desc="Native support for Xbox controllers and Windows handhelds (Steam Deck/ROG Ally)." 
            />
          </div>
        </div>
      </section>

      {/* Game Modes */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className="space-y-8">
              <h2 className="text-4xl font-black text-white uppercase italic">Built for <span className="text-accent-purple">The Abyss</span></h2>
              <p className="text-muted leading-relaxed">
                Experience a retro pixel art aesthetic meets modern neon glow. Optimized for high-fidelity 4K visuals and immersive spatial audio that brings the Ancient Ones to life.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 bg-card border border-border rounded-md">
                  <h4 className="text-white font-bold mb-1">3 Difficulty Modes</h4>
                  <p className="text-xs text-muted">From 'Mortal' to 'Insanity' for true arcade masters.</p>
                </div>
                <div className="p-4 bg-card border border-border rounded-md">
                  <h4 className="text-white font-bold mb-1">Endless Mode</h4>
                  <p className="text-xs text-muted">Test your limits against infinite waves of horror.</p>
                </div>
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-none p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase">Technical Specs</h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">OS</span>
                  <span className="text-white">Windows 10 / 11 (x64)</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Graphics</span>
                  <span className="text-white">DirectX 12 Compatible</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Controller</span>
                  <span className="text-white">Xbox / XInput Supported</span>
                </div>
                <div className="flex justify-between py-2 border-b border-border/50">
                  <span className="text-muted">Architecture</span>
                  <span className="text-white">Native x64 / ARM64</span>
                </div>
              </div>
              <div className="mt-8">
                <MicrosoftStoreBtn 
                  accentColor="bg-accent-purple" 
                  className="w-full" 
                  href={STORE_LINK}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-purple/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FaSkull} className="w-16 h-16 text-accent-purple mx-auto mb-8 animate-pulse" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Will you survive the descent?</h2>
          <p className="text-muted mb-10 text-lg">
            One-time purchase. Endless nightmares. No microtransactions. No online requirement.
          </p>
          <MicrosoftStoreBtn 
            accentColor="bg-accent-purple" 
            className="mx-auto px-16 py-6 scale-125" 
            href={STORE_LINK}
          />
        </div>
      </section>
    </div>
  );
};

export default EldritchDescent;